import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  Drawer,
  Button,
  Box,
  Group,
  Image,
  Text,
  Stack,
  Grid,
  Title,
  MantineProvider,
  createStyles,
  ScrollArea,
  Tabs,
  Tooltip,
  Alert
} from "@mantine/core";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import tinycolor from "tinycolor2";

import FilterList from "../spaces_neo/presentations/filters/FilterList";
import SpaceListItem from "../spaces_neo/presentations/SpaceListItem";
import DefaultAvatar from "../../src/images/fallback/default.png";
import Loading from "../spaces_neo/presentations/static/Loading";
import Message from "../spaces_neo/presentations/discussion/Message";
import FollowsBlock from "./FollowsBlock";

import UserModel from "../../models/User";
import DirectMessageModel from "../../models/DirectMessage";
import SpaceModel from "../../models/Space";
import MemberFilterModel from "../../models/MemberFilters";
import MessageModel from "../../models/Message";
import FollowModel from "../../models/Follow";
import SpaceNeo4jModel from "../../models/neo4j/Space";

import SettingsProfile from "../settings/ProfileFormModal";

import { setTiptapSidemenu } from "../../store/app";

import isSpaceThemed from "../../utils/IsSpaceThemed";
import { getRoot } from "../../utils/Host";

const useStyles = createStyles((theme, { colors }) => ({
  avatarMediumScreen: {
    width: 130,
    marginRight: 0,
  },
  avatar: {
    width: 130,
    marginRight: 0
  },
  userDetailsWrap: {
    alignSelf: "center",
  },
  drawerWrap: {
    backgroundColor: colors.background_color,
  },
  drawerHeader: {
    padding: "20px"
  },
  primaryButtonStyle: {
    backgroundColor: colors.primary_button_background_color,
    color: colors.primary_button_text_color,
    "&:hover": {
      backgroundColor: tinycolor(colors.primary_button_background_color)
        .darken(4)
        .toString(),
    },
  },
  buttonStyleOutline: {
    backgroundColor: "none",
    color: colors.primary_button_background_color,
    border: `1px solid ${colors.primary_button_background_color}`,
    "&:hover": {
      backgroundColor: tinycolor(colors.background_color).darken(1).toString(),
    },
  },
  themeTextColor: {
    color: colors.default_text_color,
  },
  closeButton: {
    backgroundColor: colors.background_color,
    color: colors.default_text_color,
    "&:hover": {
      backgroundColor: colors.background_color,
      color: colors.primary_button_background_color,
    },
  },
}));

const paginatorPosts = { per: 20, page: 1 };
const paginatorComments = { per: 20, page: 1 };
const paginatorMemberSpaces = { per: 20, page: 1 };
const paginatorSpaces = { per: 20, page: 0 };

export default function ProfileModal(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const space = useSelector((state) => state.space);
  const colors = useSelector((state) => state.colors);
  const user = useSelector((state) => state.user);
  const tiptapSidemenu = useSelector((state) => state.tiptapSidemenu);
  const { classes } = useStyles({ colors });

  const [profileUser, setProfileUser] = useState({});
  const [memberSpaces, setMemberSpaces] = useState([]);
  const [loadingMemberSpaces, setLoadingMemberSpaces] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [opened, setOpened] = useState(false);
  const [username, setUsername] = useState();
  const [edit, setEdit] = useState(false);
  const [isEditMember, setIsEditMember] = useState(false);
  const [filters, setFilters] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [followCounts, setFollowCounts] = useState(null);
  const [loadingFollowCounts, setLoadingFollowCounts] = useState(false);

  const openedFromUrl = useRef();
  const nomorePosts = useRef();
  const nomoreComments = useRef();
  const nomoreMemberSpaces = useRef();
  const nomoreSpaces = useRef();

  const mediumScreen = false;
  const canEditMember = ["admin", "manager"].includes(window.$currentRole);

  useEffect(() => {
    if (username) {
      onFetch();
      onFetchFilters();
      onFetchFollowCounts();
      onFetchPosts();
      onFetchMemberSpaces();
      onFetchSpaces();
    }
  }, [username]);

  useEffect(() => {
    if (openedFromUrl.current != location.pathname) {
      setOpened(false);
    }
  }, [location]);

  useEffect(() => {
    if (tiptapSidemenu && tiptapSidemenu.opened == "profile") {
      setUsername(tiptapSidemenu.option);
      setOpened(true);
    } else {
      setOpened(false);
      setLoadingPosts();
      setActiveTab(0);

      paginatorPosts.page = 1;
      paginatorComments.page = 1;
      paginatorMemberSpaces.page = 1;
      paginatorSpaces.page = 0;
      nomorePosts.current = false;
      nomoreComments.current = false;
      nomoreMemberSpaces.current = false;
      nomoreSpaces.current = false;
      setPosts([]);
      setComments([]);
      setMemberSpaces([]);
      setSpaces([]);
      setProfileUser({});
    }
  }, [tiptapSidemenu]);

  useEffect(() => {
    if (!opened) {
      setEdit(false);
      setIsEditMember(false);
      setUsername(null);
      if (tiptapSidemenu.opened == "profile") {
        dispatch(setTiptapSidemenu({ opened: "filter-members-close" }));
      }
    } else {
      openedFromUrl.current = location.pathname;
    }
  }, [opened]);

  const onFetch = () => {
    UserModel.onFetch(
      username,
      (data) => {
        setProfileUser(data);
        onFetchComments(data);
      },
      (data) => {}
    );
  };

  const onFetchFollowCounts = () => {
    setLoadingFollowCounts(true);
    FollowModel.onFetchFollowCounts(
      { user_id: username, slug: space?.slug },
      (data) => {
        setFollowCounts(data);
        setLoadingFollowCounts(false);
      },
      (data) => setLoadingFollowCounts(false)
    );
  };

  const onFetchMemberSpaces = () => {
    if (!username ||!space?.slug) return;
    setLoadingMemberSpaces(true);
    SpaceModel.onFetchUserSpaces(
      {
        scope: "member_spaces",
        slug: space.slug,
        username: username,
        paginator: paginatorMemberSpaces
      },
      (data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            const merged = [...memberSpaces, ...data];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setMemberSpaces(unique);
            if (data.length % paginatorMemberSpaces.per != 0) {
              nomoreMemberSpaces.current = true;
            }
          } else {
            nomoreMemberSpaces.current = true;
          }
          setLoadingMemberSpaces(false);
        }
      },
      (err) => {
        setLoadingMemberSpaces(false);
      }
    )
  };

  const onFetchSpaces = () => {
    const ctrl = new AbortController();
    setLoadingSpaces(true);
    SpaceNeo4jModel.onFetchByScope(
      {
        scope: "top",
        username: username,
        page: paginatorSpaces.page,
        per: paginatorSpaces.per
      },
      (data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            const merged = [...spaces, ...data];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setSpaces(unique);
            if (data.length % paginatorSpaces.per != 0) {
              nomoreSpaces.current = true;
            }
          } else {
            nomoreSpaces.current = true;
          }
        }
        setLoadingSpaces(false);
      },
      (err) => setLoadingSpaces(false),
      ctrl.signal
    );
  };

  const onFetchPosts = () => {
    if (!username || !space?.slug) return;
    setLoadingPosts(true);
    SpaceModel.onFetchUserSpaces(
      {
        scope: "posts",
        slug: space.slug,
        username: username,
        paginator: paginatorPosts
      },
      (data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            const merged = [...posts, ...data];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setPosts(unique);
            if (data.length % paginatorPosts.per != 0) {
              nomorePosts.current = true;
            }
          } else {
            nomorePosts.current = true;
          }
        }
        setLoadingPosts(false);
      },
      (err) => {
        setLoadingPosts(false);
      }
    );
  };

  const onFetchComments = (data) => {
    const user_id = data?.id;
    if (!user_id || !space?.slug) return;
    setLoadingComments(true);
    MessageModel.onFetchComments(
      space.slug,
      user_id,
      paginatorComments,
      (data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            const merged = [...comments, ...data];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setComments(unique);
            if (data.length % paginatorComments.per != 0) {
              nomoreComments.current = true;
            }
          } else {
            nomoreComments.current = true;
          }
        }
        setLoadingComments(false);
      },
      (err) => {
        setLoadingComments(false);
      }
    );
  };

  const onFetchFilters = () => {
    if (!window.$fetchedTopSlug) return;
    MemberFilterModel.onFetch(
      window.$fetchedTopSlug,
      username,
      (data) => {
        if (Array.isArray(data)) {
          setFilters(data);
        }
      },
      (error) => {}
    );
  };

  const onDirectMessage = () => {
    if (!user.authenticated) {
      navigate(`/user/start`);
      return;
    }
    DirectMessageModel.onFetchOrCreate(
      profileUser.id,
      (data) => {
        dispatch(
          setTiptapSidemenu({ opened: "direct", option: data.space.slug, allow_reflections: true })
        );
      },
      (data) => {}
    );
  };

  const onCreateMemberFilter = (pod_member_filter_id, value) => {
    if (!window.$fetchedTopSlug) return;
    MemberFilterModel.onCreate(
      window.$fetchedTopSlug,
      {
        username,
        pod_member_filter_id,
        value
      },
      (data) => onFetchFilters(),
      (error) => {}
    );
  };

  const onUpdateMemberFilter = (pod_member_filter_id, member_filter_id, value) => {
    if (!window.$fetchedTopSlug) return;
    MemberFilterModel.onUpdate(
      window.$fetchedTopSlug,
      member_filter_id,
      {
        pod_member_filter_id,
        value
      },
      (data) => onFetchFilters(),
      (error) => {}
    );
  };

  const onRemoveMemberFilter = (member_filter_id) => {
    if (!member_filter_id || !window.$fetchedTopSlug) return;
    MemberFilterModel.onDestroy(
      window.$fetchedTopSlug,
      member_filter_id,
      (data) => onFetchFilters(),
      (error) => {}
    );
  };

  const onPressFollow = () => {
    if (!profileUser?.id) return;
    const action = profileUser.is_followed ? FollowModel.onUnfollow : FollowModel.onFollow;
    action(
      profileUser.id,
      (data) => {
        if (data.success) {
          onFetch();
          onFetchFollowCounts();
        }
      },
      (err) => {}
    );
  };

  const renderPostsTab = () => {
    return (
      <Tabs.Tab label="Posts">
        {loadingPosts && posts.length === 0 && <Loading />}
        {posts.length > 0 ? (
          <Box mb={60} mt={10}>
            <Grid>
              {posts.map((item) => {
                return (
                  <Grid.Col sm={12}>
                    <SpaceListItem
                      key={`space-list-item-${item.slug}`}
                      space={item}
                      viewAs={"post"}
                      showAuthor={true}
                      lineClamp={[1, 2]}
                      settings={{
                        noActions: true,
                        listShowComments: true,
                        listShowThumbnail: true,
                        listShowMembers: true,
                        hidePin: true,
                        listShowTimestamp: true,
                        listShowPreviewText: true
                      }}
                    />
                  </Grid.Col>
                );
              })
            }
            </Grid>
            {!nomorePosts.current && (
              <MantineProvider theme={{ primaryShade: 8 }}>
                {loadingPosts ? (
                  <Loading />
                ) : (
                  <Button
                    variant="outline"
                    className={classes.buttonStyleOutline}
                    radius="xl"
                    mt={30}
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                    onClick={() => {
                      paginatorPosts.page++;
                      onFetchPosts();
                    }}
                  >
                    {"Load more"}
                  </Button>
                )}
              </MantineProvider>
            )}
          </Box>
        ) : loadingPosts ? null : (
          <Alert color="gray" radius="md" mt={20}>
            {"This person hasn’t posted anything"}
          </Alert>
        )}
      </Tabs.Tab>
    );
  };

  const renderCommentsTab = () => {
    return (
      <Tabs.Tab label="Comments">
        {loadingComments && comments.length === 0 && <Loading />}
        {comments.length > 0 ? (
          <Box mb={60}>
            {comments.map((message) => {
              return (
                <Message
                  key={`message_${message.id}`}
                  spaceId={message.pod_slug}
                  spaceVersion={message.pod_version}
                  messageId={message.id}
                  htmlId={`${message.type}_${message.id}`}
                  messageCreatedAt={message.created_at}
                  messageTitle={message.post ? message.post.subject : null}
                  messageContent={
                    message.version == "new"
                      ? message.content
                      : message.message_body
                  }
                  messageUnseenCount={message.unseen_count}
                  tags={message.tags}
                  reflections={message.reflections}
                  user={message.user ? message.user : {}}
                  onOpenReflections={() => {
                    let url = "";
                    if (message.pod_version === "interactive-discussion") {
                      url = `${getRoot()}/space/${message.pod_slug}/content?pod_message_id=${message.id}&reflection_id=true&interactive_slug=${message.pod_slug}`;
                    } else {
                      url = `${getRoot()}/space/${message.pod_slug}/${message.pod_version}?pod_message_id=${message.id}&reflection_id=true`;
                    }
                    window.location = url;
                  }}
                  version={message.version}
                  type={"pod_message"}
                  text={message.text}
                  isLiked={message.is_liked}
                  likesCount={message.likes_count}
                  pod_name={message.pod_name}
                  hideActions={true}
                  canOpenMessage={true}
                />
              );
            })}

            {!nomoreComments.current && (
              <MantineProvider theme={{ primaryShade: 8 }}>
                {loadingComments ? (
                  <Loading />
                ) : (
                  <Button
                    variant="outline"
                    className={classes.buttonStyleOutline}
                    radius="xl"
                    mt={30}
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                    onClick={() => {
                      paginatorComments.page++;
                      onFetchComments(profileUser);
                    }}
                  >
                    {"Load more"}
                  </Button>
                )}
              </MantineProvider>
            )}
          </Box>
        ) : loadingComments ? null : (
          <Alert color="gray" radius="md" mt={20}>
            {"This person hasn’t commented on anything"}
          </Alert>
        )}
      </Tabs.Tab>
    );
  };

  if (!user) {
    return null;
  }

  const hasMemberFilters = Array.isArray(filters) ? filters.filter((f) => f.value).length > 0 : false;

  return (
    <Drawer
      classNames={{
        drawer: classes.drawerWrap,
        closeButton: classes.closeButton,
        header: classes.drawerHeader
      }}
      opened={opened}
      onClose={() => setOpened(false)}
      title={
        <Text className={classes.themeTextColor} weight="bold">
          {t("users.profileModal.profile")}
        </Text>
      }
      padding={0}
      pl={20}
      size={650}
      zIndex={9999}
    >
      {(edit || isEditMember) && (
        <Button
          className={classes.buttonStyleOutline}
          variant="outline"
          color="gray"
          radius="xl"
          mb={20}
          ml={20}
          leftIcon={<FontAwesomeIcon icon={solid("arrow-left")} />}
          onClick={() => {
            setEdit(false);
            setIsEditMember(false);
          }}
        >
          {"Go back"}
        </Button>
      )}
      <ScrollArea style={{ height: "100%" }} offsetScrollbars={true} pl={20} pr={5}>
        <Box style={{ marginBottom: "50px" }} mt={20} pb={50}>
          {!edit && (
            <Box sx={{ paddingBottom: 10 }}>
              <Group noWrap>
                <Image
                  className={mediumScreen ? classes.avatarMediumScreen : classes.avatar}
                  radius={300}
                  src={profileUser.avatar ? profileUser.avatar.url : DefaultAvatar}
                  alt={profileUser.username}
                />
                <Box className={classes.userDetailsWrap}>
                  <Stack ml={5} spacing={0}>
                    <Group spacing={0} mb={5}>
                      <Title
                        className={classes.themeTextColor}
                        order={3}
                        sx={(theme) => ({
                          fontWeight: 900,
                          marginRight: 10,
                          lineHeight: 1,
                        })}
                      >
                        {profileUser.full_name}
                      </Title>
                      <Text
                        className={classes.themeTextColor}
                        color="dimmed"
                        size="xl"
                      >
                        @{profileUser.username}
                      </Text>
                    </Group>
                    <FollowsBlock
                      user={profileUser}
                      colors={colors}
                      followers_count={followCounts?.followers_count}
                      followees_count={followCounts?.followees_count}
                      prana_count={followCounts?.prana_count}
                      posts_in_count={followCounts?.posts_in_count}
                      likes_in_count={followCounts?.likes_in_count}
                      top_parent_name={followCounts?.top_parent_name}
                      onFetchFollowCounts={() => {
                        onFetch();
                        onFetchFollowCounts();
                      }}
                    />
                    <Group spacing={5}>
                      {profileUser && user.id != profileUser.id ? (
                        <>
                          {typeof profileUser?.is_followed === "boolean" && (
                            <MantineProvider theme={{ primaryShade: 6 }}>
                              <Button
                                className={classes.primaryButtonStyle}
                                radius="xl"
                                size="sm"
                                onClick={onPressFollow}
                              >
                                {profileUser.is_followed ? "Unfollow" : "Follow"}
                              </Button>
                            </MantineProvider>
                          )}
                          {!isEditMember && (
                            <Tooltip label="Send message" withArrow zIndex={99999}>
                              <Button
                                className={classes.primaryButtonStyle}
                                radius="xl"
                                size="sm"
                                onClick={() => onDirectMessage()}
                              >
                                {t("users.profileModal.message")}
                              </Button>
                            </Tooltip>
                          )}
                          {canEditMember && (
                            <Tooltip label={isEditMember ? "View profile" : "Edit profile"} withArrow zIndex={99999}>
                              <Button
                                className={classes.primaryButtonStyle}
                                radius="xl"
                                size="sm"
                                onClick={() => setIsEditMember(!isEditMember)}
                              >
                                <FontAwesomeIcon icon={solid("edit")} />
                              </Button>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <MantineProvider theme={{ primaryShade: 6 }}>
                          <Tooltip label="Edit profile" withArrow zIndex={99999}>
                            <Button
                              className={classes.primaryButtonStyle}
                              radius="xl"
                              size="sm"
                              onClick={() => setEdit(true)}
                            >
                              <FontAwesomeIcon icon={solid("edit")} />
                            </Button>
                          </Tooltip>
                        </MantineProvider>
                      )}
                      {!isEditMember && (
                        <MantineProvider theme={{ primaryShade: 6 }}>
                          <Tooltip label="Open profile" withArrow zIndex={99999}>
                            <Button
                              component={Link}
                              to={`/${profileUser.username}`}
                              className={classes.primaryButtonStyle}
                              radius="xl"
                              size="sm"
                              onClick={() => setOpened(false)}
                            >
                              <FontAwesomeIcon icon={solid("external-link-alt")} />
                            </Button>
                          </Tooltip>
                        </MantineProvider>
                      )}
                    </Group>
                  </Stack>
                </Box>
              </Group>
            </Box>
          )}

          {!!edit && (
            <SettingsProfile
              inModal={true}
              FilterComponent={
                <FilterList
                  type="profile"
                  data={filters}
                  colors={colors}
                  isEditMode={true}
                  onCreateFilter={onCreateMemberFilter}
                  onUpdateFilter={onUpdateMemberFilter}
                  onRemoveFilter={onRemoveMemberFilter}
                />
              }
            />
          )}

          {!edit && (
            <FilterList
              type="profile"
              data={filters}
              colors={colors}
              isEditMode={isEditMember}
              onCreateFilter={onCreateMemberFilter}
              onUpdateFilter={onUpdateMemberFilter}
              onRemoveFilter={onRemoveMemberFilter}
            />
          )}
          
          {!(edit || isEditMember) && (
            <>
              {typeof profileUser.bio === "string" && profileUser.bio.length > 0 && (
                <Box mt={20}>
                  {hasMemberFilters && (
                    <Group spacing={0} mb={5}>
                      <Box sx={{ width: 16 }} align="center">
                        <FontAwesomeIcon
                          icon={solid("user")}
                          color={isSpaceThemed(colors) ? colors?.default_text_color : "grey"}
                        />
                      </Box>
                      <Text
                        size="sm"
                        ml={5}
                        style={{ color: isSpaceThemed(colors) ? colors?.default_text_color : "grey" }}
                      >
                        {"BIO"}
                      </Text>
                    </Group>
                  )}
                  <Text className={classes.themeTextColor} size="sm">
                    {profileUser.bio}
                  </Text>
                </Box>
              )}

              <Tabs
                active={activeTab}
                onTabChange={setActiveTab}
                variant="unstyled"
                styles={(theme) => ({
                  tabControl: {
                    color: isSpaceThemed(colors) ?
                      tinycolor(colors.default_text_color).darken(40).toString()
                      : theme.colors.gray[6],
                    fontSize: theme.fontSizes.md,
                    fontWeight: "bold",
                    padding: 0,
                    marginRight: 24,
                    marginTop: 20,
                    borderBottom: "3px solid transparent"
                  },
                  tabActive: {
                    borderBottom: `3px solid ${isSpaceThemed(colors) ? colors.default_text_color : "#000"}`,
                    color: isSpaceThemed(colors) ? colors.default_text_color : "#000"
                  },
                })}
              >

                {posts.length === 0 && !loadingPosts ? renderCommentsTab() : renderPostsTab()}

                {posts.length === 0 && !loadingPosts ? renderPostsTab() : renderCommentsTab()}

                <Tabs.Tab label="Member of">
                  {loadingMemberSpaces && memberSpaces.length === 0 && <Loading />}
                  {memberSpaces.length > 0 ? (
                    <Box mb={60} mt={10}>
                      <Grid>
                        {memberSpaces.map((item) => {
                          return (
                            <Grid.Col sm={12}>
                              <SpaceListItem
                                key={`space-list-item-${item.slug}`}
                                space={item}
                                lineClamp={2}
                                styleTitle={{ fontSize: "1.4em" }}
                              />
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                      {!nomoreMemberSpaces.current && (
                        <MantineProvider theme={{ primaryShade: 8 }}>
                          {loadingMemberSpaces ? (
                            <Loading />
                          ) : (
                            <Button
                              variant="outline"
                              className={classes.buttonStyleOutline}
                              radius="xl"
                              mt={30}
                              style={{ left: "50%", transform: "translateX(-50%)" }}
                              onClick={() => {
                                paginatorMemberSpaces.page++;
                                onFetchMemberSpaces();
                              }}
                            >
                              {"Load more"}
                            </Button>
                          )}
                        </MantineProvider>
                      )}
                    </Box>
                  ) : loadingMemberSpaces ? null : (
                    <Alert color="gray" radius="md" mt={20}>
                      {"This person is not a member of any spaces"}
                    </Alert>
                  )}
                </Tabs.Tab>

                <Tabs.Tab label="Spaces">
                  {loadingSpaces && spaces.length === 0 && <Loading />}
                  {spaces.length > 0 ? (
                    <Box mb={60} mt={10}>
                      <Grid>
                        {spaces.map((item) => {
                          return (
                            <Grid.Col sm={12}>
                              <SpaceListItem
                                key={`space-list-item-${item.slug}`}
                                space={item}
                                lineClamp={2}
                                styleTitle={{ fontSize: "1.4em" }}
                              />
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                      {!nomoreSpaces.current && (
                        <MantineProvider theme={{ primaryShade: 8 }}>
                          {loadingSpaces ? (
                            <Loading />
                          ) : (
                            <Button
                              variant="outline"
                              className={classes.buttonStyleOutline}
                              radius="xl"
                              mt={30}
                              style={{ left: "50%", transform: "translateX(-50%)" }}
                              onClick={() => {
                                paginatorSpaces.page++;
                                onFetchSpaces();
                              }}
                            >
                              {"Load more"}
                            </Button>
                          )}
                        </MantineProvider>
                      )}
                    </Box>
                  ) : loadingSpaces ? null : (
                    <Alert color="gray" radius="md" mt={20}>
                      {"This person is not a member of any spaces"}
                    </Alert>
                  )}
                </Tabs.Tab>
              </Tabs>
            </>
          )}

        </Box>
        <Box style={{ height: "100px" }} />
      </ScrollArea>
    </Drawer>
  );
}
